import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react';

import { storage } from '../firebase';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from './UserContext';

export const DocumentsContext = createContext(null);

// Teacher documents
export const TEACHER_DOCUMENT_TYPES = [
  'תעודת זהות',
  'תעודת אקדמית',
  'תעודת הוראה',
  'קורות חיים',
  'תיאום מס',
  'אישור ניהול חשבון',
];

// Supplier documents
export const SUPPLIER_DOCUMENT_TYPES = [
  'אישור ניהול חשבון',
  'תעודת עוסק מורשה',
  'אישור ניכוי מס במקור',
];

// Used by Sidebar, ProfileCard, AdminConfirmDocuments, etc.
export const allDocumentTypes = [
  ...new Set([
    ...TEACHER_DOCUMENT_TYPES,
    ...SUPPLIER_DOCUMENT_TYPES,
  ]),
];

const API_BASE = "https://www.doroosonline.co.il";

const mapServerDoc = (row = {}) => ({
  id: row.id,
  type: row.type,
  customName: row.custom_name ?? row.customName ?? '',
  fileName: row.file_name ?? row.fileName ?? '',
  url: row.url,
  status: row.status ?? 'NO_RESPONSE',
  notes: row.notes ?? '',
  taxPercent: row.tax_percent ?? null,
  taxValidFrom: row.tax_valid_from ?? null,
  taxValidTo: row.tax_valid_to ?? null,
  taxReceivedAt: row.tax_received_at ?? null,
  date: row.created_at
    ? new Date(row.created_at).toLocaleDateString('he-IL')
    : new Date().toLocaleDateString('he-IL'),
});

const getAuthHeaders = () => {
  const token =
    (typeof window !== 'undefined' && (window.__userToken || '')) ||
    localStorage.getItem('token') ||
    localStorage.getItem('jwt') ||
    localStorage.getItem('authToken') ||
    '';

  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const safeDeleteFromStorage = async (fileName) => {
  try {
    const fileRef = ref(storage, `docs/${fileName}`);
    await deleteObject(fileRef);

    return { ok: true };
  } catch (err) {
    if (err?.code === 'storage/object-not-found') {
      console.warn(
        '[Documents] Storage object not found; continuing DB delete.',
        fileName
      );

      return { ok: true, warn: 'not-found' };
    }

    console.warn(
      '[Documents] Storage delete failed (will still delete DB):',
      err
    );

    return { ok: false, error: err };
  }
};

export const DocumentsProvider = ({ children }) => {
  const { user, userType } = useUser();

  const effectiveRole = user?.role || userType || 'student';

  // Document list according to current role
  const documentTypes = useMemo(() => {
    if (effectiveRole === 'supplier') {
      return SUPPLIER_DOCUMENT_TYPES;
    }

    if (effectiveRole === 'teacher') {
      return TEACHER_DOCUMENT_TYPES;
    }

    return [];
  }, [effectiveRole]);

  const [documents, setDocuments] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);

  const [viewedFileUrl, setViewedFileUrl] = useState(null);
  const [selectedDocForStatus, setSelectedDocForStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [adminDocuments, setAdminDocuments] = useState([]);

  const [adminFilters, setAdminFilters] = useState({
    status: 'NO_RESPONSE',
    search: '',
    type: '',
    user_id: '',
  });

  const [adminPage, setAdminPage] = useState(1);
  const [adminLimit, setAdminLimit] = useState(4);
  const [adminTotal, setAdminTotal] = useState(0);

  const [adminCounts, setAdminCounts] = useState({
    NO_RESPONSE: 0,
    APPROVED: 0,
    REJECTED: 0,
    NEEDS_REVISION: 0,
    TOTAL: 0,
  });

  const recomputeAvailableTypes = useCallback(
    (docs) => {
      const used = new Set(
        (Array.isArray(docs) ? docs : []).map((d) => d.type)
      );

      setAvailableTypes(
        documentTypes.filter((type) => !used.has(type))
      );
    },
    [documentTypes]
  );

  useEffect(() => {
    recomputeAvailableTypes(documents);
  }, [documents, recomputeAvailableTypes]);

  const loadDocuments = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/documents`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const text = await res.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }

      if (!res.ok || !data?.ok) {
        throw new Error(
          data?.error ||
          text ||
          res.statusText ||
          'טעינת המסמכים נכשלה'
        );
      }

      const rows = Array.isArray(data.documents)
        ? data.documents
        : [];

      const mapped = rows.map(mapServerDoc);

      setDocuments(mapped);
      recomputeAvailableTypes(mapped);
    } catch (err) {
      console.error('Load documents failed:', err);

      setDocuments([]);
      recomputeAvailableTypes([]);
    } finally {
      setIsLoading(false);
    }
  }, [recomputeAvailableTypes]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const viewFile = useCallback((url) => {
    setViewedFileUrl(url);
  }, []);

  const closeFileView = useCallback(() => {
    setViewedFileUrl(null);
  }, []);

  const viewStatus = useCallback((doc) => {
    setSelectedDocForStatus(doc);
  }, []);

  const closeStatus = useCallback(() => {
    setSelectedDocForStatus(null);
  }, []);

  const addDocument = useCallback(
    async (file, type, customName) => {
      // Prevent teacher/supplier from uploading an invalid document type
      if (!documentTypes.includes(type)) {
        throw new Error('סוג המסמך אינו מורשה עבור סוג משתמש זה');
      }

      setIsLoading(true);

      let storageRefPath;
      let stage = 'start';

      try {
        stage = 'firebase:upload';

        const ext = (file.name.split('.').pop() || '').toLowerCase();
        const fileName = `${uuidv4()}.${ext}`;

        storageRefPath = ref(storage, `docs/${fileName}`);

        await uploadBytes(storageRefPath, file);

        stage = 'firebase:url';

        const url = await getDownloadURL(storageRefPath);

        stage = 'api:post';

        const res = await fetch(`${API_BASE}/api/documents`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            type,
            customName,
            fileName,
            url,
          }),
        });

        stage = 'api:read';

        const raw = await res.text();

        let data = null;

        try {
          data = JSON.parse(raw);
        } catch {
          data = null;
        }

        if (!res.ok || !data?.ok) {
          try {
            await deleteObject(storageRefPath);
          } catch {}

          const serverMsg =
            (data && (data.error || data.message)) ||
            raw ||
            res.statusText ||
            '';

          throw new Error(
            serverMsg || `API error (${res.status})`
          );
        }

        const saved = mapServerDoc(data.document);

        setDocuments((prev) => {
          const next = [
            ...(Array.isArray(prev) ? prev : []),
            saved,
          ];

          recomputeAvailableTypes(next);

          return next;
        });
      } catch (err) {
        console.error(
          'addDocument failed at stage:',
          stage,
          err
        );

        let msg = err?.message || 'שגיאה לא ידועה';

        if (stage.startsWith('firebase')) {
          msg = 'שגיאה בהעלאה ל-Firebase: ' + msg;
        }

        if (stage.startsWith('api')) {
          msg = 'שגיאה בשמירת המסמך בשרת: ' + msg;
        }

        throw new Error(msg);
      } finally {
        setIsLoading(false);
      }
    },
    [documentTypes, recomputeAvailableTypes]
  );

  const deleteDocument = useCallback(
    async (doc) => {
      setIsLoading(true);

      try {
        const storageResult =
          await safeDeleteFromStorage(doc.fileName);

        const res = await fetch(
          `${API_BASE}/api/documents/${doc.id}`,
          {
            method: 'DELETE',
            headers: getAuthHeaders(),
          }
        );

        const text = await res.text();

        let data;

        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }

        if (!res.ok || data?.ok === false) {
          throw new Error(
            data?.error ||
            text ||
            res.statusText ||
            'מחיקת המסמך בשרת נכשלה'
          );
        }

        setDocuments((prev) => {
          const next = (
            Array.isArray(prev) ? prev : []
          ).filter((d) => d.id !== doc.id);

          recomputeAvailableTypes(next);

          return next;
        });

        if (!storageResult.ok) {
          console.warn(
            '[Documents] המסמך נמחק מהמערכת אך לא נמחק מהענן.',
            storageResult.error
          );
        }
      } catch (err) {
        console.error('deleteDocument failed:', err);

        throw new Error(
          err?.message ||
          'התרחשה שגיאה בעת מחיקת המסמך, נסה שוב.'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [recomputeAvailableTypes]
  );

  const hideDocument = useCallback(
    async (doc) => {
      setIsLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/api/documents/${doc.id}/hide`,
          {
            method: 'PATCH',
            headers: getAuthHeaders(),
          }
        );

        const text = await res.text();

        let data;

        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }

        if (!res.ok || data?.ok === false) {
          throw new Error(
            data?.error ||
            text ||
            res.statusText ||
            'הסתרת המסמך נכשלה'
          );
        }

        setDocuments((prev) => {
          const next = (
            Array.isArray(prev) ? prev : []
          ).filter((d) => d.id !== doc.id);

          recomputeAvailableTypes(next);

          return next;
        });
      } finally {
        setIsLoading(false);
      }
    },
    [recomputeAvailableTypes]
  );

  const loadAdminCounts = useCallback(async () => {
    setIsLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/api/documents/admin/counts`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      const text = await res.text();

      let data;

      try {
        data = JSON.parse(text);
      } catch {
        data = null;
      }

      if (!res.ok || !data?.ok) {
        throw new Error(
          data?.error ||
          text ||
          res.statusText ||
          'טעינת ספירות מסמכים נכשלה'
        );
      }

      const c = data.counts || {};

      setAdminCounts({
        NO_RESPONSE: Number(c.NO_RESPONSE || 0),
        APPROVED: Number(c.APPROVED || 0),
        REJECTED: Number(c.REJECTED || 0),
        NEEDS_REVISION: Number(c.NEEDS_REVISION || 0),
        TOTAL: Number(c.TOTAL || 0),
      });
    } catch (err) {
      console.error('loadAdminCounts failed:', err);

      setAdminCounts({
        NO_RESPONSE: 0,
        APPROVED: 0,
        REJECTED: 0,
        NEEDS_REVISION: 0,
        TOTAL: 0,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAdminDocuments = useCallback(
    async (
      filters = adminFilters,
      page = adminPage,
      limit = adminLimit
    ) => {
      setIsLoading(true);

      try {
        const f = filters || {};
        const p = Math.max(1, Number(page || 1));
        const l = Math.max(1, Number(limit || 4));

        const q = new URLSearchParams();

        if (f.status) q.set('status', f.status);
        if (f.search) q.set('search', f.search);
        if (f.type) q.set('type', f.type);
        if (f.user_id) q.set('user_id', String(f.user_id));

        q.set('page', String(p));
        q.set('limit', String(l));

        const res = await fetch(
          `${API_BASE}/api/documents/admin?${q.toString()}`,
          {
            method: 'GET',
            headers: getAuthHeaders(),
          }
        );

        const text = await res.text();

        let data;

        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }

        if (!res.ok || !data?.ok) {
          throw new Error(
            data?.error ||
            text ||
            res.statusText ||
            'טעינת מסמכי אדמין נכשלה'
          );
        }

        const rows = Array.isArray(data.documents)
          ? data.documents
          : [];

        const mapped = rows.map((row) => ({
          ...mapServerDoc(row),

          user: {
            id: row.user_id,
            email: row.email,
            firstName: row.first_name,
            lastName: row.last_name,
            phone: row.phone,
          },
        }));

        setAdminDocuments(mapped);
        setAdminPage(Number(data.page || p));
        setAdminLimit(Number(data.limit || l));
        setAdminTotal(Number(data.total || 0));
      } catch (err) {
        console.error('loadAdminDocuments failed:', err);

        setAdminDocuments([]);
        setAdminTotal(0);
      } finally {
        setIsLoading(false);
      }
    },
    [adminFilters, adminPage, adminLimit]
  );

  const adminUpdateDocument = useCallback(
    async (docId, payload) => {
      setIsLoading(true);

      try {
        const res = await fetch(
          `${API_BASE}/api/documents/admin/${docId}`,
          {
            method: 'PATCH',
            headers: getAuthHeaders(),
            body: JSON.stringify(payload || {}),
          }
        );

        const text = await res.text();

        let data;

        try {
          data = JSON.parse(text);
        } catch {
          data = null;
        }

        if (!res.ok || !data?.ok) {
          throw new Error(
            data?.error ||
            text ||
            res.statusText ||
            'עדכון מסמך נכשל'
          );
        }

        const updated = {
          ...mapServerDoc(data.document),

          user:
            (
              Array.isArray(adminDocuments)
                ? adminDocuments
                : []
            ).find((d) => d.id === docId)?.user || null,
        };

        setAdminDocuments((prev) =>
          (
            Array.isArray(prev)
              ? prev
              : []
          ).map((d) =>
            d.id === docId ? updated : d
          )
        );

        return updated;
      } finally {
        setIsLoading(false);
      }
    },
    [adminDocuments]
  );

  const value = useMemo(
    () => ({
      documents,
      availableTypes,
      documentTypes,
      effectiveRole,

      addDocument,
      deleteDocument,
      hideDocument,

      viewedFileUrl,
      viewFile,
      closeFileView,

      viewStatus,
      selectedDocForStatus,
      closeStatus,

      isLoading,
      reload: loadDocuments,

      adminDocuments: Array.isArray(adminDocuments)
        ? adminDocuments
        : [],

      loadAdminDocuments,
      adminUpdateDocument,

      adminFilters,
      setAdminFilters,

      adminPage,
      setAdminPage,

      adminLimit,
      setAdminLimit,

      adminTotal,

      adminCounts,
      loadAdminCounts,
    }),
    [
      documents,
      availableTypes,
      documentTypes,
      effectiveRole,
      addDocument,
      deleteDocument,
      hideDocument,
      viewedFileUrl,
      viewFile,
      closeFileView,
      viewStatus,
      selectedDocForStatus,
      closeStatus,
      isLoading,
      loadDocuments,
      adminDocuments,
      loadAdminDocuments,
      adminUpdateDocument,
      adminFilters,
      adminPage,
      adminLimit,
      adminTotal,
      adminCounts,
      loadAdminCounts,
    ]
  );

  return (
    <DocumentsContext.Provider value={value}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = () => {
  const ctx = useContext(DocumentsContext);

  if (!ctx) {
    throw new Error(
      'useDocuments must be used within DocumentsProvider'
    );
  }

  return ctx;
};